import {FormGroup} from '@angular/forms';

/**
 * Updates a given form with valid fields from the provided data object.
 * Converts ISO date strings to the 'YYYY-MM-DD' format for date fields.
 * Only fields present in the form and having non-null, non-undefined values will be updated.
 * The updated fields are also marked as dirty.
 *
 * @template T - The type of the data object.
 * @param {FormGroup} form - The form group to be updated.
 * @param {Partial<T>} data - The object containing values to update the form with.
 */
export function updateFormWithValidFields<T>(form: FormGroup, data: Partial<T>): void
{
    const validData = Object.keys(data).reduce((acc, key) =>
    {
        if (form.controls.hasOwnProperty(key) && data[key] !== undefined && data[key] !== null)
        {
            const control = form.get(key);

            // Check if the form control is a date input
            if (control && control.value instanceof Date || key.toLowerCase().includes('date'))
            {
                acc[key] = formatDateToYMD(data[key]);
            }
            else
            {
                acc[key] = data[key];
            }
        }
        return acc;
    }, {} as Partial<T>);

    form.patchValue(validData);
    Object.keys(validData).forEach((key) => form.get(key)?.markAsDirty());
}

/**
 * Converts an ISO date string to 'YYYY-MM-DD' format.
 *
 * @param {any} dateValue - The date value (ISO string or Date object).
 * @returns {string} - The formatted date string.
 */
export function formatDateToYMD(dateValue: any): string
{
    if (!dateValue)
    {
        return '';
    }

    // ISO instant (has a time component): convert to the local calendar date so
    // the picker shows the same day the merchant sees in the results table.
    if (typeof dateValue === 'string' && dateValue.includes('T'))
    {
        return formatLocalYMD(new Date(dateValue)) ?? '';
    }

    if (dateValue instanceof Date)
    {
        return formatLocalYMD(dateValue) ?? '';
    }

    // Already a plain 'YYYY-MM-DD' string – leave it untouched.
    return dateValue;
}

/**
 * Builds the lower (start-of-day) bound for a date-range filter from a picked
 * calendar date. The date is interpreted as local wall-clock time (no 'Z'
 * suffix) and serialised to a UTC ISO string, so the boundary lines up with the
 * local day the merchant sees in the results table.
 *
 * @param {string | Date} date - The picked calendar date (e.g. '2026-07-07').
 * @returns {string | null} - The UTC ISO string for local start-of-day, or null.
 */
export function toUtcIsoAtLocalStartOfDay(date: string | Date | null | undefined): string | null
{
    return toLocalIso(date, '00:00:00');
}

/**
 * Builds the upper (end-of-day) bound for a date-range filter from a picked
 * calendar date. The date is interpreted as local wall-clock time (no 'Z'
 * suffix) and serialised to a UTC ISO string.
 *
 * @param {string | Date} date - The picked calendar date (e.g. '2026-07-07').
 * @returns {string | null} - The UTC ISO string for local end-of-day, or null.
 */
export function toUtcIsoAtLocalEndOfDay(date: string | Date | null | undefined): string | null
{
    return toLocalIso(date, '23:59:59.999');
}

/**
 * Serialises a picked calendar date at a given local time-of-day to a UTC ISO string.
 * The date is interpreted as local wall-clock time (no 'Z' suffix), so the resulting
 * boundary lines up with the local day the merchant sees in the results table.
 *
 * @param {string | Date} date - The picked calendar date (e.g. '2026-07-07').
 * @param {string} timeOfDay - The local time-of-day suffix (e.g. '00:00:00').
 * @returns {string | null} - The UTC ISO string, or null for empty/invalid input.
 */
function toLocalIso(date: string | Date | null | undefined, timeOfDay: string): string | null
{
    if (!date)
    {
        return null;
    }
    // A Date's toString() yields e.g. "Tue Jul 07 2026 … GMT+0200 (SAST)", which is not
    // parseable once a time suffix is appended. Normalise a Date to 'YYYY-MM-DD' first.
    const ymd = date instanceof Date ? formatLocalYMD(date) : date;
    if (!ymd)
    {
        return null;
    }
    const parsed = new Date(`${ymd}T${timeOfDay}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * Formats a date to a local 'YYYY-MM-DD' string (based on the browser's local
 * time zone), for populating date-picker inputs from stored ISO instants.
 *
 * @param {Date} date - The date to format.
 * @returns {string | null} - The local 'YYYY-MM-DD' string, or null if invalid.
 */
export function formatLocalYMD(date: Date): string | null
{
    if (!date || Number.isNaN(date.getTime()))
    {
        return null;
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}